import type { TmsContextType } from '@awazevr/mfe-common.contexts.tms'

import { OptimizelyState } from '@/context/optimizely-decisions-context'
import { trackClientSideOptimizelyEvent } from '@/utils/optimizely/trackOptimizelyEvent'

import type { ExplorationTile } from './data'

export type ExplorationInteraction = 'click' | 'impression'

export const EXPLORATION_CAROUSEL_SHOWN_EVENT =
  'age___search___exploration_suggestions_carousel_shown'
export const EXPLORATION_CAROUSEL_CLICK_EVENT =
  'age___search___exploration_suggestions_carousel_click'

// Regex-free on purpose (avoids the SonarCloud ReDoS hotspot, S7764/S5852).
const toSnakeCase = (value: string): string => {
  const lower = value.trim().toLowerCase()
  const parts: string[] = []
  let current = ''

  for (const char of lower) {
    const isAlphaNumeric =
      (char >= 'a' && char <= 'z') || (char >= '0' && char <= '9')

    if (isAlphaNumeric) {
      current += char
    } else if (current !== '') {
      parts.push(current)
      current = ''
    }
  }

  if (current !== '') {
    parts.push(current)
  }

  return parts.join('_')
}

// e.g. exploration_suggestions_widget_cornwall_st_ives_2 (searched place + tile)
export const buildExplorationClickLabel = (
  searchedPlace: ExplorationTile,
  tile: ExplorationTile,
  position: number,
): string =>
  `exploration_suggestions_widget_${toSnakeCase(searchedPlace.regionName)}_${toSnakeCase(tile.name)}_${position}`

// e.g. exploration_suggestions_widget_newquay (searched place, carousel-level)
export const buildExplorationImpressionLabel = (
  searchedPlace: ExplorationTile,
): string =>
  `exploration_suggestions_widget_${toSnakeCase(searchedPlace.regionName)}`

// Shared Awaze opti_track dataLayer event. tms is undefined without a provider.
const pushOptiTrack = (
  tms: TmsContextType | undefined,
  trackAction: ExplorationInteraction,
  trackLabel: string,
): void => {
  tms?.updateDataLayer?.('link', 'opti_track', {
    track_action: trackAction,
    track_label: trackLabel,
  })
}

export const trackExplorationClick = (
  searchedPlace: ExplorationTile,
  tile: ExplorationTile,
  position: number,
  optimizelyState: OptimizelyState,
  tms: TmsContextType | undefined,
): void => {
  pushOptiTrack(
    tms,
    'click',
    buildExplorationClickLabel(searchedPlace, tile, position),
  )
  void trackClientSideOptimizelyEvent(
    EXPLORATION_CAROUSEL_CLICK_EVENT,
    optimizelyState,
  )
}

export const trackExplorationImpression = (
  searchedPlace: ExplorationTile,
  optimizelyState: OptimizelyState,
  tms: TmsContextType | undefined,
): void => {
  pushOptiTrack(
    tms,
    'impression',
    buildExplorationImpressionLabel(searchedPlace),
  )
  void trackClientSideOptimizelyEvent(
    EXPLORATION_CAROUSEL_SHOWN_EVENT,
    optimizelyState,
  )
}
